import React, { FC, useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { MatchTurbinePathParams } from '../../../shared/models/matchTurbinePathParams';
import useTurbinePath from '../../Shared/UseTurbinePathHook';
import axios from '../../../axios';
import styles from './SequenceProcessStatus.module.scss';
import ThumbnailCreationStatus from './ThumbnailCreationStatus';
import Button from '../../Shared/Button/Button';
import Table from 'react-bootstrap/Table';
import { ToastAnt } from '../../Shared/Toast/ToastAnt';
import { SequenceStatus } from '../../../shared/models/sequenceStatus.enum';

interface TurbineThumbnailInfo {
  path: string;
  statuses: Array<ThumbnailStatusModel>;
}
interface ThumbnailStatusModel {
  bladeBladeSide: string;
  newStatus: SequenceStatus;
}

const SequenceProcessStatus: FC<RouteComponentProps<MatchTurbinePathParams>> = ({ history, match }) => {
  const [path, setPath] = useState('');
  const [statuses, setStatuses] = useState<Array<ThumbnailStatusModel>>([]);
  const [site, inspection, turbine] = useTurbinePath(match.params);
  const [anyNotCompleted, setAnyNotCompleted] = useState(false);

  useEffect(() => {
    if (site && inspection && turbine) getStatuses();
  }, [site, inspection, turbine]);

  useEffect(() => {
    let id: any;

    if (anyNotCompleted) {
      id = setInterval(() => {
        getStatuses();
      }, 10000);
    }

    return () => {
      if (id) {
        clearInterval(id);
      }
    };
  }, [anyNotCompleted]);

  const getStatuses = async () => {
    const url = `Info/thumbnails/${site}/${inspection}/${turbine}`;
    try {
      let response = await axios.get<TurbineThumbnailInfo>(url);

      setPath(response.data.path);
      setStatuses(response.data.statuses);
      setAnyNotCompleted(response.data.statuses.some(x => x.newStatus < SequenceStatus.MlStitchingCompleted));
    } catch (error) {}
  };

  const startThumbnailCreation = async (bladeBladeSide: string) => {
    const url = `info/thumbnails/run-thumbnails/${site}/${inspection}/${turbine}/${bladeBladeSide.split('/')[0]}/${bladeBladeSide.split('/')[1]}`;

    let status = statuses.find(x => x.bladeBladeSide === bladeBladeSide);

    if (!status) {
      return;
    }

    try {
      status.newStatus = SequenceStatus.MlStitchingInProgress;
      setAnyNotCompleted(true);
      setStatuses([...statuses]);

      await axios.post(url, null);

      ToastAnt('Starting...');
    } catch (error) {
      ToastAnt('An error occured...');
    }
  };

  const rerunThumbnailCreation = async (bladeBladeSide: string) => {
    const url = `info/thumbnails/rerun-thumbnails/${site}/${inspection}/${turbine}/${bladeBladeSide.split('/')[0]}/${bladeBladeSide.split('/')[1]}`;

    let status = statuses.find(x => x.bladeBladeSide === bladeBladeSide);

    if (!status) {
      return;
    }

    try {
      status.newStatus = SequenceStatus.MlStitchingInProgress;
      setAnyNotCompleted(true);
      setStatuses([...statuses]);

      await axios.post(url, null);

      ToastAnt('Starting...');
    } catch (error) {
      ToastAnt('An error occured...');
    }
  };

  let statusesList = statuses.map(x => {
    return (
      <tr key={x.bladeBladeSide} className={styles.statusRow}>
        <td className={styles.bladeValue}>
          <span>{x.bladeBladeSide.split('/')[0]}</span>
        </td>
        <td className={styles.bladeSideValue}>
          <span>{x.bladeBladeSide.split('/')[1]}</span>
        </td>
        <td>
          <ThumbnailCreationStatus status={x.newStatus} />
        </td>
        <td className={styles.actions}>
          <Button
            title='Start'
            onClick={() => startThumbnailCreation(x.bladeBladeSide)}
            disabled={x.newStatus > SequenceStatus.WaitingForMlStitching}
            type='button'
          />
          {x.newStatus === SequenceStatus.MlStitchingInProgress && (
            <Button
              title='Restart'
              onClick={() => rerunThumbnailCreation(x.bladeBladeSide)}
              disabled={false}
              type='button'
              style={{ marginLeft: '10px' }}
            />
          )}
        </td>
      </tr>
    );
  });

  return (
    <div className={`${styles.thumbnailContainer} container`}>
      {site && inspection && turbine && (
        <div className={styles.path}>
          <div>
            <span className={styles.pathTitle}>Site: </span>
            <span>{path.split('/')[0]}</span>
          </div>
          <div>
            <span className={styles.pathTitle}>Inspection: </span>
            <span>{path.split('/')[1]}</span>
          </div>
          <div>
            <span className={styles.pathTitle}>Turbine: </span>
            <span>{path.split('/')[2]}</span>
          </div>
        </div>
      )}
      <Table striped bordered hover className={styles.thumbnailTable}>
        <thead>
          <tr>
            <th>Blade</th>
            <th>Blade Side</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {statuses.length > 0 ? (
            statusesList
          ) : (
            <tr>
              <td colSpan={4}>There is no data for this turbine.</td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default SequenceProcessStatus;
